import {useEmojisQuery} from '@github-ui/emoji-autocomplete/use-emojis-query'
import type {Emoji, Mentionable, Reference, SavedReply} from '@github-ui/markdown-editor'
import useIsMounted from '@github-ui/use-is-mounted'
import {useEffect, useState} from 'react'

import {useGetSavedReplies} from '../api/saved-replies'
import {useGetMentionSuggestions} from '../api/suggestions/mention-suggestions'
import {useGetReferenceSuggestions} from '../api/suggestions/reference-suggestions'
import type {ApiMarkdownSubject} from '../api/types'

interface MarkdownSuggestionsData {
  mentions: Mentionable[] | 'loading'
  references: Reference[] | 'loading'
  emojis: Emoji[] | 'loading'
  savedReplies: SavedReply[]
}

const emptySuggestionData: Omit<MarkdownSuggestionsData, 'emojis'> = {
  mentions: 'loading',
  references: 'loading',
  savedReplies: [],
}

export function useMarkdownSuggestions(
  apiSubject: Partial<ApiMarkdownSubject> = {},
  loadSuggestions: boolean,
): MarkdownSuggestionsData {
  const isMounted = useIsMounted()

  const getMentionSuggestions = useGetMentionSuggestions(apiSubject)
  const getReferenceSuggestions = useGetReferenceSuggestions(apiSubject)

  const getSavedReplies = useGetSavedReplies(apiSubject)

  const [data, setData] = useState<Omit<MarkdownSuggestionsData, 'emojis'>>(emptySuggestionData)

  useEffect(() => {
    const fetchSuggestions = async () => {
      const [mentions, references, savedReplies] = await Promise.all([
        getMentionSuggestions(),
        getReferenceSuggestions(),
        getSavedReplies(),
      ])

      if (isMounted() && (mentions.length || references.length || savedReplies.length)) {
        setData({mentions, references, savedReplies})
      }
    }

    if (!loadSuggestions) {
      return
    }

    void fetchSuggestions()
  }, [getMentionSuggestions, getReferenceSuggestions, getSavedReplies, isMounted, loadSuggestions])

  const emojiSuggestionsQuery = useEmojisQuery(loadSuggestions)

  return {
    ...data,
    emojis: emojiSuggestionsQuery.isPending ? 'loading' : (emojiSuggestionsQuery.data ?? defaultEmojisData),
  }
}

const defaultEmojisData: Emoji[] = []
